import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import EntityTable from '../components/Listing/EntityTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH, faCirclePlus } from '@fortawesome/free-solid-svg-icons';
import InputText from '../components/Form/Fields/InputText';
import Select from '../components/Form/Fields/Select';
import FabGroup from '../components/FabGroup';
import { NavLink } from 'react-router-dom';
import CreateLaboratoryModal from '../components/Modal/CreateLaboratoryModal';
import LaboratoryLogo from '../components/Laboratory/LaboratoryLogo';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';

const TYPE_OPTIONS = [
    { value: '', label: 'Tous' },
    { value: 'LABORATOIRE', label: 'Laboratoire' },
    { value: 'REPARTITEUR', label: 'Répartiteur' },
];

const TYPE_STYLES = {
    LABORATOIRE: { background: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
    REPARTITEUR: { background: '#faf5ff', color: '#7c3aed', border: '#e9d5ff' },
};

const TYPE_LABELS = {
    LABORATOIRE: 'Laboratoire',
    REPARTITEUR: 'Répartiteur',
};

export default function Suppliers() {
    const { setTitle, setTitleIcon } = usePageTitle();
    const { t } = useTranslation();
    const { hasRight } = useAuth();
    const canCreate = hasRight('SUPPLIERS_CREATE');
    const canEdit = hasRight('SUPPLIERS_EDIT');
    const tableRef = useRef(null);
    const [showModal, setShowModal] = useState(false);
    const [editLab, setEditLab] = useState(null);

    useEffect(() => {
        setTitle(`${t('Suppliers')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">Laboratoires et fournisseurs de médicaments</span>`);
        document.title = t('Suppliers');
        setTitleIcon('ti-truck-delivery');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useBreadcrumbSetup([{ label: t('Suppliers'), url: '/suppliers' }]);

    const handleSaved = () => tableRef.current?.reload();

    const openCreate = () => { setEditLab(null); setShowModal(true); };
    const openEdit   = (row) => { setEditLab(row); setShowModal(true); };

    const columns = [
        {
            Header: '#',
            accessor: 'id',
            sortable: true,
            className: 'text-center',
            filterable: { component: InputText },
            Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>{row.id}</span>,
        },
        {
            Header: 'Laboratoire',
            accessor: 'name',
            skeleton: 'media', // loading placeholder: avatar + two lines
            sortable: true,
            filterable: { component: InputText },
            Cell: (row) => (
                <NavLink
                    to={`/suppliers/${row.id}`}
                    className="cell-name"
                    style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                >
                    <LaboratoryLogo id={row.id} name={row.name} />
                    {row.name}
                </NavLink>
            ),
        },
        {
            Header: 'Email',
            accessor: 'email',
            filterable: { component: InputText },
            Cell: (row) => row.email
                ? <span style={{ fontSize: 13, color: '#374151' }}>{row.email}</span>
                : <span className="cell-muted">—</span>,
        },
        {
            Header: 'Téléphone',
            accessor: 'telephone',
            filterable: { component: InputText },
            Cell: (row) => row.telephone
                ? <span style={{ fontSize: 13, color: '#374151' }}>{row.telephone}</span>
                : <span className="cell-muted">—</span>,
        },
        {
            Header: 'Type',
            accessor: 'type',
            filterable: { component: Select, options: TYPE_OPTIONS, placeholder: 'Tous les types' },
            Cell: (row) => {
                const s = TYPE_STYLES[row.type] ?? TYPE_STYLES.LABORATOIRE;
                return (
                    <span style={{
                        fontSize: 11, fontWeight: 600,
                        background: s.background, color: s.color,
                        border: `1px solid ${s.border}`,
                        borderRadius: 20, padding: '3px 10px',
                        whiteSpace: 'nowrap',
                    }}>
                        {TYPE_LABELS[row.type] ?? 'Laboratoire'}
                    </span>
                );
            },
        },
        {
            Header: 'Date de création',
            accessor: 'creationDate',
            sortable: true,
            className: 'text-center',
            Cell: (row) => row.creationDate
                ? <span style={{ fontSize: 13, color: '#374151' }}>{row.creationDate}</span>
                : <span className="cell-muted">—</span>,
        },
        {
            Header: '',
            accessor: 'actions',
            className: 'col-actions',
            Cell: (row) => (
                <div className="cell-dropdown">
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        {canEdit && (
                            <li>
                                <button className="dropdown-item" onClick={() => openEdit(row)}>Modifier</button>
                            </li>
                        )}
                    </ul>
                </div>
            ),
        },
    ];

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
    // — reuses the actions column's own dropdown so both views offer identical actions.
    const actionsColumn = columns.find((c) => c.accessor === 'actions');
    const renderSupplierCard = (row) => {
        const s = TYPE_STYLES[row.type] ?? TYPE_STYLES.LABORATOIRE;
        return (
            <div className="card h-100 shadow-sm">
                <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                    <span style={{
                        alignSelf: 'flex-start', fontSize: 11, fontWeight: 600,
                        background: s.background, color: s.color,
                        border: `1px solid ${s.border}`,
                        borderRadius: 20, padding: '3px 10px',
                    }}>
                        {TYPE_LABELS[row.type] ?? 'Laboratoire'}
                    </span>

                    <NavLink
                        to={`/suppliers/${row.id}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}
                    >
                        <LaboratoryLogo id={row.id} name={row.name} />
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>{row.name}</span>
                    </NavLink>

                    {row.email && <span style={{ fontSize: 11.5, color: '#6b7280' }}>{row.email}</span>}
                    {row.telephone && <span style={{ fontSize: 11.5, color: '#9ca3af' }}>{row.telephone}</span>}

                    <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                        <span style={{ fontSize: 11.5, color: '#9ca3af' }}>{row.creationDate || ''}</span>
                        {actionsColumn?.Cell(row)}
                    </div>
                </div>
            </div>
        );
    };

    const buttons = canCreate ? [
        { label: 'Nouveau laboratoire', className: 'btn-primary', icon: faCirclePlus, onClick: openCreate },
    ] : [];

    return (
        <>
            <EntityTable
                ref={tableRef}
                endpoint={`${API_BASE_URL}/api/v1/laboratories`}
                columns={columns}
                defaultSort="name"
                defaultDirection="asc"
                pageSize={20}
                emptyMessage="Aucun fournisseur"
                searchable={true}
                multiSelect={true}
                searchPlaceholder="Rechercher un laboratoire ..."
                enableCardView={true}
                renderCard={renderSupplierCard}
            />
            <FabGroup buttons={buttons} />

            <CreateLaboratoryModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onSaved={handleSaved}
                laboratory={editLab}
            />
        </>
    );
}
